import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { mediaUrl } from "../public/qr/QrMedia";
import { edgeColor, darken } from "./bannerColor";

export const BANNER_RATIO = { mobile: "3 / 2", wide: "2 / 1", label: "3:2 (1200 × 800 px) on phones · 2:1 on wide screens" };
const AUTO_MS = 4000;

/** Banner frame used by the Burger King Style homepage: fixed ratio, one slide visible, artwork fully contained, matching colour fill. */
export const bannerSrc = (b) => mediaUrl(b.image_url, "large");

export const useBannerColors = (banners, fallback) => {
  const [colors, setColors] = useState({});
  useEffect(() => {
    let alive = true;
    banners.forEach((b) => {
      if (b.bg_color) { setColors((c) => ({ ...c, [b.id]: b.bg_color })); return; }
      edgeColor(bannerSrc(b)).then((hex) => alive && setColors((c) => ({ ...c, [b.id]: hex || fallback })));
    });
    return () => { alive = false; };
  }, [banners, fallback]);
  return colors;
};

export const BkBanner = ({ banners, onOpen, paused, colors, onActiveColor }) => {
  const n = banners.length;
  const [i, setI] = useState(0);
  const touch = useRef(null);
  const moved = useRef(false);
  useEffect(() => { if (i >= n) setI(0); }, [n, i]);
  useEffect(() => {
    if (n < 2 || paused) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % n), AUTO_MS);
    return () => clearInterval(t);
  }, [n, paused]);
  const active = banners[i] || banners[0];
  const color = colors[active?.id];
  useEffect(() => { onActiveColor && onActiveColor(color || null); }, [color, onActiveColor]);
  if (!n) return null;
  const go = (d) => setI((x) => (x + d + n) % n);
  const onTouchStart = (e) => { touch.current = e.touches[0].clientX; moved.current = false; };
  const onTouchMove = (e) => { if (touch.current != null && Math.abs(e.touches[0].clientX - touch.current) > 8) moved.current = true; };
  const onTouchEnd = (e) => { if (touch.current == null) return; const dx = e.changedTouches[0].clientX - touch.current; touch.current = null; if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); };
  return (
    <section className="bkb" aria-label="Offers" data-testid="offer-carousel" style={{ "--bkb-bg": color || "var(--bk-bg)" }}>
      <div className="bkb-frame" data-testid="bk-banner-frame" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        {banners.map((b, idx) => (
          <button key={b.id} type="button" className={`bkb-slide ${idx === i ? "active" : ""}`} aria-hidden={idx !== i} tabIndex={idx === i ? 0 : -1}
            onClick={() => { if (!moved.current) onOpen(b); }} data-testid={`offer-banner-${b.id}`} data-active={idx === i}>
            <img src={bannerSrc(b)} alt={b.title || "Offer"} loading={idx === 0 ? "eager" : "lazy"} draggable={false} />
          </button>
        ))}
        {n > 1 && (
          <>
            <button type="button" className="bkb-arrow left" onClick={() => go(-1)} aria-label="Previous offer" data-testid="offer-carousel-prev"><ChevronLeft size={18} /></button>
            <button type="button" className="bkb-arrow right" onClick={() => go(1)} aria-label="Next offer" data-testid="offer-carousel-next"><ChevronRight size={18} /></button>
          </>
        )}
      </div>
      {(active.title || active.subtitle) && (
        <button type="button" className="bkb-caption" onClick={() => onOpen(active)} data-testid="offer-banner-caption">
          <span><b>{active.title}</b>{active.subtitle && <small>{active.subtitle}</small>}</span>
        </button>
      )}
      {n > 1 && <div className="bkb-dots" data-testid="offer-carousel-dots">{banners.map((b, idx) => <button key={b.id} type="button" aria-label={`Offer ${idx + 1}`} aria-current={idx === i} onClick={() => setI(idx)} data-testid={`offer-carousel-dot-${idx}`} />)}</div>}
    </section>
  );
};

/** Preview of how an uploaded banner image will sit in the homepage frame (used in the banner editor). */
export const BannerFramePreview = ({ src, bgColor, onAutoColor }) => {
  const [auto, setAuto] = useState(null);
  const [dim, setDim] = useState(null);
  useEffect(() => { setAuto(null); if (src) edgeColor(src).then((c) => { setAuto(c); onAutoColor && onAutoColor(c); }); }, [src]); // eslint-disable-line react-hooks/exhaustive-deps
  const bg = bgColor || auto || "#444444";
  const ratio = dim ? dim.w / dim.h : null;
  const mismatch = ratio != null && Math.abs(ratio - 1.5) > 0.08;
  return (
    <div className="space-y-2" data-testid="banner-frame-preview">
      <div className="bkb-frame bkb-frame-preview" style={{ background: darken(bg) }}>
        {src ? <img src={src} alt="Banner preview" onLoad={(e) => setDim({ w: e.target.naturalWidth, h: e.target.naturalHeight })} style={{ width: "100%", height: "100%", objectFit: "contain" }} /> : <span className="text-xs text-white/80 grid place-items-center h-full">Upload an image to preview</span>}
      </div>
      <p className="text-[11px] text-slate-500" data-testid="banner-frame-hint">
        Recommended {BANNER_RATIO.label}. {dim ? `This image is ${dim.w} × ${dim.h} px (${ratio.toFixed(2)}:1). ` : ""}
        {mismatch ? "Its proportions differ from 3:2, so the matching colour fills the remaining space — the artwork is never cropped." : dim ? "It fills the frame edge to edge." : ""}
      </p>
    </div>
  );
};
